import "./styles.css";
import { buildGameplayWorld, buildLandingWeapon, createEngine } from "./engine";
import { createCombatState, applyAttack } from "./combat";
import { ResonanceEngine } from "./audio/resonance";
import { ObsidianHud } from "./ui/obsidian";
import { haptic } from "./systems/haptic";
import { EchoRecorder } from "./social/echo";
import { requestForgePreview, requestForgeWeapon, requestReforge, type EssenceInput, type ForgePreview, type ForgedWeapon } from "./forge";
import { generateSector } from "./world";
import { API_BASE, SUPPORTED_LANGS } from "./config";
import { applyVisualManifestToDocument, describeVisualManifest, loadStage8VisualManifest, stage8HeroPath, stage8RuntimeGlbs, stage8RuntimeVisuals, stage8SkinlandsGameplayPath } from "./assets/visualManifest";
import { combatStateVisuals, momentumVisuals, woundStateVisuals } from "./ui/stateVisuals";
import { soulsmithColors, soulsmithMotion } from "./ui/visualTokens";

void boot();

async function boot() {
const visualManifestResult = await loadStage8VisualManifest();
const visualManifest = visualManifestResult.manifest;
applyVisualManifestToDocument(visualManifest);

const canvas = document.getElementById("crucible-scene") as HTMLCanvasElement | null;
const hudHost = document.getElementById("pixi-hud") as HTMLElement | null;
const publicSite = document.getElementById("public-site") as HTMLElement | null;
const enterTriggers = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-enter-crucible]"));
const offlineBadge = document.getElementById("offline-badge") as HTMLElement | null;
const binding = document.getElementById("soul-binding") as HTMLFormElement | null;
const soulName = document.getElementById("soul-name") as HTMLInputElement | null;
const soulSecret = document.getElementById("soul-secret") as HTMLInputElement | null;
const bindingError = document.getElementById("binding-error") as HTMLElement | null;
const forgeTransition = document.getElementById("forge-transition") as HTMLElement | null;
const forgeShell = document.getElementById("forge-shell") as HTMLElement | null;
const forgePreviewButton = document.getElementById("forge-preview") as HTMLButtonElement | null;
const forgeConfirmButton = document.getElementById("forge-confirm") as HTMLButtonElement | null;
const forgeReforgeButton = document.getElementById("forge-reforge") as HTMLButtonElement | null;
const forgeCloseButton = document.getElementById("forge-close") as HTMLButtonElement | null;
const forgeStatus = document.getElementById("forge-status") as HTMLElement | null;
const forgePreviewType = document.getElementById("forge-preview-type") as HTMLElement | null;
const forgePreviewDamage = document.getElementById("forge-preview-damage") as HTMLElement | null;
const forgePreviewWeight = document.getElementById("forge-preview-weight") as HTMLElement | null;
const forgePreviewDurability = document.getElementById("forge-preview-durability") as HTMLElement | null;
const forgePreviewAbility = document.getElementById("forge-preview-ability") as HTMLElement | null;
const forgePreviewSeed = document.getElementById("forge-preview-seed") as HTMLElement | null;
const forgeResultName = document.getElementById("forge-result-name") as HTMLElement | null;
const forgeResultGeneration = document.getElementById("forge-result-generation") as HTMLElement | null;
const forgeResultTraits = document.getElementById("forge-result-traits") as HTMLElement | null;
const reforgePanel = document.getElementById("reforge-panel") as HTMLElement | null;
const reforgeEssenceSelect = document.getElementById("reforge-essence-select") as HTMLButtonElement | null;
const reforgeConfirmButton = document.getElementById("reforge-confirm") as HTMLButtonElement | null;
const reforgeCancelButton = document.getElementById("reforge-cancel") as HTMLButtonElement | null;
const reforgePreviewText = document.getElementById("reforge-preview-text") as HTMLElement | null;
const reforgeDamageChange = document.getElementById("reforge-damage-change") as HTMLElement | null;
const reforgeGenerationChange = document.getElementById("reforge-generation-change") as HTMLElement | null;
const essenceStashGrid = document.querySelector(".essence-stash-grid") as HTMLElement | null;
const inventoryShell = document.getElementById("inventory-shell") as HTMLElement | null;
const inventoryGrid = document.querySelector(".inventory-grid") as HTMLElement | null;
const inventoryEquipButton = document.getElementById("inventory-equip") as HTMLButtonElement | null;
const inventoryReforgeButton = document.getElementById("inventory-reforge") as HTMLButtonElement | null;
const inventoryCloseButton = document.getElementById("inventory-close") as HTMLButtonElement | null;
const inventoryDetailName = document.getElementById("inventory-detail-name") as HTMLElement | null;
const inventoryDetailType = document.getElementById("inventory-detail-type") as HTMLElement | null;
const inventoryDetailDamage = document.getElementById("inventory-detail-damage") as HTMLElement | null;
const inventoryDetailWeight = document.getElementById("inventory-detail-weight") as HTMLElement | null;
const inventoryDetailDurability = document.getElementById("inventory-detail-durability") as HTMLElement | null;
const inventoryDetailGeneration = document.getElementById("inventory-detail-generation") as HTMLElement | null;
const inventoryDetailTraits = document.getElementById("inventory-detail-traits") as HTMLElement | null;
const profileShell = document.getElementById("profile-shell") as HTMLElement | null;
const profileCloseButton = document.getElementById("profile-close") as HTMLButtonElement | null;

if (!canvas || !hudHost || !publicSite || !enterTriggers.length || !offlineBadge || !binding || !soulName || !soulSecret || !bindingError || !forgeTransition || !forgeShell || !forgePreviewButton || !forgeConfirmButton || !forgeReforgeButton || !forgeCloseButton || !forgeStatus || !forgePreviewType || !forgePreviewDamage || !forgePreviewWeight || !forgePreviewDurability || !forgePreviewAbility || !forgePreviewSeed || !forgeResultName || !forgeResultGeneration || !forgeResultTraits) {
  throw new Error("Soulsmith shell failed to bind.");
}
const forgeUi = {
  shell: forgeShell as HTMLElement,
  previewButton: forgePreviewButton as HTMLButtonElement,
  confirmButton: forgeConfirmButton as HTMLButtonElement,
  reforgeButton: forgeReforgeButton as HTMLButtonElement,
  closeButton: forgeCloseButton as HTMLButtonElement,
  status: forgeStatus as HTMLElement,
  previewType: forgePreviewType as HTMLElement,
  previewDamage: forgePreviewDamage as HTMLElement,
  previewWeight: forgePreviewWeight as HTMLElement,
  previewDurability: forgePreviewDurability as HTMLElement,
  previewAbility: forgePreviewAbility as HTMLElement,
  previewSeed: forgePreviewSeed as HTMLElement,
  resultName: forgeResultName as HTMLElement,
  resultGeneration: forgeResultGeneration as HTMLElement,
  resultTraits: forgeResultTraits as HTMLElement
};

const marketShell = document.getElementById("market-shell") as HTMLElement;
const marketListings = document.getElementById("market-listings") as HTMLElement;
const marketTabs = document.querySelectorAll<HTMLButtonElement>(".market-tab");
const marketListItemButton = document.getElementById("market-list-item") as HTMLButtonElement;
const marketCloseButton = document.getElementById("market-close") as HTMLButtonElement;
const marketStatus = document.getElementById("market-status") as HTMLElement;
const hollowWares = document.getElementById("hollow-wares") as HTMLElement;

const battlePassShell = document.getElementById("battle-pass-shell") as HTMLElement;
const passTrack = document.getElementById("pass-track") as HTMLElement;
const passTier = document.getElementById("pass-tier") as HTMLElement;
const passUpgradeButton = document.getElementById("pass-upgrade") as HTMLButtonElement;
const passCloseButton = document.getElementById("pass-close") as HTMLButtonElement;
const passStatus = document.getElementById("pass-status") as HTMLElement;
const dailyChallenges = document.getElementById("daily-challenges") as HTMLElement;

const covenShell = document.getElementById("coven-shell") as HTMLElement;
const covenInfo = document.getElementById("coven-info") as HTMLElement;
const covenCreateButton = document.getElementById("coven-create") as HTMLButtonElement;
const covenCloseButton = document.getElementById("coven-close") as HTMLButtonElement;
const covenStatus = document.getElementById("coven-status") as HTMLElement;
const huntStatus = document.getElementById("hunt-status") as HTMLElement;
const huntDamageButton = document.getElementById("hunt-damage") as HTMLButtonElement;

const socialShell = document.getElementById("social-shell") as HTMLElement;
const friendsList = document.getElementById("friends-list") as HTMLElement;
const friendDiscoverButton = document.getElementById("friend-discover") as HTMLButtonElement;
const socialCloseButton = document.getElementById("social-close") as HTMLButtonElement;
const socialStatus = document.getElementById("social-status") as HTMLElement;
const giftTargets = document.getElementById("gift-targets") as HTMLElement;
const hudLayer = hudHost;
const publicSiteRoot = publicSite;
const bindingForm = binding;
const soulNameInput = soulName;
const transitionLayer = forgeTransition;
document.body.classList.add("is-public");

const lang = navigator.language.split("-")[0];
const activeLang = SUPPORTED_LANGS.includes(lang as (typeof SUPPORTED_LANGS)[number]) ? lang : "en";
document.documentElement.lang = activeLang;

const { engine, scene, gpuTier } = createEngine(canvas);
const landing = buildLandingWeapon(scene);
const gameplayWorld = buildGameplayWorld(scene, landing.camera, {
  skinlandsBackdropPath: stage8SkinlandsGameplayPath(visualManifest),
  runtimeGlbs: stage8RuntimeGlbs(visualManifest),
  runtimeVisuals: stage8RuntimeVisuals(visualManifest)
});
const hud = new ObsidianHud(hudLayer);
const resonance = new ResonanceEngine();
const echo = new EchoRecorder();
let combat = createCombatState();
let lastEcho = 0;
let pillarMenuOpen = false;
hudLayer.classList.add("is-muted");
const visualState = {
  titleBlockCount: landing.titleBlockCount,
  forgeTransitionPlayed: false,
  forgeTransitionCount: 0,
  currentMomentumState: "low" as keyof typeof momentumVisuals,
  runeMarkers: [] as Array<{ x: number; y: number; timestamp: number; type: "light" | "heavy" }>
};
const sceneState = {
  animationTimeScale: scene.animationTimeScale,
  pillarMenuOpen
};

// Stage 6: Combat Visual Contracts
function applyMomentumVisuals(momentum: number) {
  let momentumState: keyof typeof momentumVisuals = "low";
  if (momentum >= 100) momentumState = "overdrive";
  else if (momentum >= 75) momentumState = "fracture";
  else if (momentum >= 50) momentumState = "trail";
  else if (momentum >= 25) momentumState = "ember";
  
  if (momentumState !== visualState.currentMomentumState) {
    visualState.currentMomentumState = momentumState;
    const state = momentumVisuals[momentumState];
    hud.updateMomentumState(state);
  }
}

function createRuneMarker(type: "light" | "heavy", x?: number, y?: number) {
  const marker = {
    x: x ?? 0,
    y: y ?? 0,
    timestamp: performance.now(),
    type
  };
  visualState.runeMarkers.push(marker);
  
  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) {
    hud.spawnStaticRuneMarker(marker, type);
  } else {
    hud.spawnRuneMarker(marker, type);
  }
  
  // Use existing resonance methods instead of playCombatSound
  resonance.setMomentum(combat.momentum);
}

function applyStaminaVisuals(stamina: number) {
  if (stamina < 25) {
    hud.setStaminaState("ash"); // Low stamina visual state per Stage 6 requirements
  } else {
    hud.setStaminaState("normal");
  }
}

function createHitFeedback(kind: "light" | "heavy" | "dodge") {
  const visuals = kind === "heavy" ? combatStateVisuals.heavyAttack : combatStateVisuals.lightAttack;
  hud.spawnHitVFX(visuals);
}

// Stage 6: Add click/tap markers for mouse/touch input
canvas.addEventListener("click", (event) => {
  if (!document.body.classList.contains("is-in-game")) return;
  
  const rect = canvas.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  
  createRuneMarker("light", x, y);
  combat = applyAttack(combat, "light");
  applyMomentumVisuals(combat.momentum);
  applyStaminaVisuals(combat.stamina);
  createHitFeedback("light");
  haptic("LIGHT_ATTACK");
  resonance.setMomentum(combat.momentum);
});
const forgeEssences: EssenceInput[] = [
  { essence_type: "dragon", tier: "rare" },
  { essence_type: "wolf", tier: "uncommon" },
  { essence_type: "knight", tier: "common" }
];
const reforgeEssence: EssenceInput = { essence_type: "titan", tier: "uncommon" };
let forgePreview: ForgePreview | null = null;
let forgedWeapon: ForgedWeapon | null = null;
const forgeState = {
  visible: false,
  socketCount: forgeEssences.length,
  previewRequested: false,
  previewFromServer: false,
  confirmRequested: false,
  confirmFromServer: false,
  reforgeRequested: false,
  reforgeFromServer: false,
  serverValuesDisplayed: false,
  weaponType: "",
  generation: -1,
  scarState: "unforged",
  error: ""
};

(window as Window & { __soulsmithVisualState?: typeof visualState }).__soulsmithVisualState = visualState;
(window as Window & { __soulsmithSceneState?: typeof sceneState }).__soulsmithSceneState = sceneState;
(window as Window & { __soulsmithForgeState?: typeof forgeState }).__soulsmithForgeState = forgeState;
(window as Window & { __soulsmithManifestState?: ReturnType<typeof describeVisualManifest> }).__soulsmithManifestState = describeVisualManifest(visualManifest, visualManifestResult.loaded, visualManifestResult.error);

hud.render(combat.momentum, combat.stamina);

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isInGame() {
  return document.body.classList.contains("is-in-game");
}

function closeAllShells() {
  forgeUi.shell.hidden = true;
  marketShell.hidden = true;
  battlePassShell.hidden = true;
  covenShell.hidden = true;
  socialShell.hidden = true;
}

async function openMarketShell() {
  if (!isInGame()) return;
  closeAllShells();
  marketShell.hidden = false;
  marketStatus.textContent = "Loading listings...";
  try {
    const data = await requestMarketListings("all");
    marketListings.innerHTML = "";
    if (data.listings.length === 0) {
      marketListings.innerHTML = `<p class="market-empty">No listings at the altar.</p>`;
    } else {
      data.listings.slice(0, 10).forEach((listing: unknown) => {
        const item = document.createElement("div");
        item.className = "market-listing-item";
        item.innerHTML = `<strong>Market Item</strong><span>${JSON.stringify(listing).slice(0, 60)}</span>`;
        marketListings.appendChild(item);
      });
    }
    marketStatus.textContent = "";
  } catch (e) {
    marketStatus.textContent = "Failed to load listings.";
  }
}

async function openBattlePassShell() {
  if (!isInGame()) return;
  closeAllShells();
  battlePassShell.hidden = false;
  passStatus.textContent = "Loading Crucible Pass...";
  try {
    const [seasonData, challengesData] = await Promise.all([
      requestBattlePassSeason(),
      requestDailyChallenges()
    ]);
    passTier.textContent = `Tier ${(seasonData.progress as { tier: number }).tier}`;
    passTrack.innerHTML = "";
    challengesData.challenges.forEach((challenge: unknown) => {
      const item = document.createElement("div");
      item.className = "challenge-item";
      item.innerHTML = `<strong>Challenge</strong><span>${JSON.stringify(challenge).slice(0, 60)}</span>`;
      dailyChallenges.appendChild(item);
    });
    passStatus.textContent = "";
  } catch (e) {
    passStatus.textContent = "Failed to load Crucible Pass.";
  }
}

async function openCovenShell() {
  if (!isInGame()) return;
  closeAllShells();
  covenShell.hidden = false;
  covenStatus.textContent = "Loading Coven data...";
  try {
    const huntData = await requestHuntStatus("weekly");
    huntStatus.innerHTML = `<div class="hunt-status-item"><strong>Weekly Hunt</strong><span>${JSON.stringify(huntData.status).slice(0, 60)}</span></div>`;
    covenInfo.innerHTML = `<p class="coven-empty">No Coven answers to your call. Form one to begin.</p>`;
    covenStatus.textContent = "";
  } catch (e) {
    covenStatus.textContent = "Failed to load Coven data.";
  }
}

async function openSocialShell() {
  if (!isInGame()) return;
  closeAllShells();
  socialShell.hidden = false;
  socialStatus.textContent = "Loading Soul Bonds...";
  try {
    const friendsData = await requestFriends();
    friendsList.innerHTML = "";
    if (friendsData.friends.length === 0) {
      friendsList.innerHTML = `<p class="friends-empty">No Soul Bonds forged yet.</p>`;
    } else {
      friendsData.friends.slice(0, 10).forEach((friend: unknown) => {
        const item = document.createElement("div");
        item.className = "friend-item";
        item.innerHTML = `<strong>Bonded Smith</strong><span>${JSON.stringify(friend).slice(0, 60)}</span>`;
        friendsList.appendChild(item);
      });
    }
    giftTargets.innerHTML = `<p class="gift-empty">Select a bonded Smith to gift Cinders.</p>`;
    socialStatus.textContent = "";
  } catch (e) {
    socialStatus.textContent = "Failed to load Soul Bonds.";
  }
}

function setLandingMeshVisible(visible: boolean) {
  landing.titleRoot.setEnabled(visible);
  landing.blade.setEnabled(visible);
  landing.guard.setEnabled(visible);
  landing.hilt.setEnabled(visible);
  for (const light of landing.lights) light.setEnabled(visible);
}

function setPillarMenu(open: boolean) {
  pillarMenuOpen = open;
  scene.animationTimeScale = open ? 0.2 : 1;
  sceneState.pillarMenuOpen = open;
  sceneState.animationTimeScale = scene.animationTimeScale;
  if (open) hud.showPillars();
  else hud.hidePillars();
}

function publishForgeState() {
  (window as Window & { __soulsmithForgeState?: typeof forgeState }).__soulsmithForgeState = forgeState;
}

function sessionToken() {
  return sessionStorage.getItem("soulsmith.session") ?? "";
}

async function requestMarketListings(type: string = "all") {
  const response = await fetch(`${API_BASE}/market/listings?type=${type}`, {
    headers: { "Authorization": `Bearer ${sessionToken()}` }
  });
  if (!response.ok) throw new Error("Market listings unavailable");
  return response.json() as Promise<{ listings: unknown[] }>;
}

async function requestBattlePassSeason() {
  const response = await fetch(`${API_BASE}/battle-pass/season`, {
    headers: { "Authorization": `Bearer ${sessionToken()}` }
  });
  if (!response.ok) throw new Error("Battle Pass season unavailable");
  return response.json() as Promise<{ season: unknown; progress: unknown }>;
}

async function requestDailyChallenges() {
  const response = await fetch(`${API_BASE}/challenges/daily`, {
    headers: { "Authorization": `Bearer ${sessionToken()}` }
  });
  if (!response.ok) throw new Error("Daily challenges unavailable");
  return response.json() as Promise<{ challenges: unknown[] }>;
}

async function requestFriends() {
  const response = await fetch(`${API_BASE}/friends`, {
    headers: { "Authorization": `Bearer ${sessionToken()}` }
  });
  if (!response.ok) throw new Error("Friends list unavailable");
  return response.json() as Promise<{ friends: unknown[]; caps: unknown }>;
}

async function requestCovenDetails(covenId: string) {
  const response = await fetch(`${API_BASE}/coven/${covenId}`, {
    headers: { "Authorization": `Bearer ${sessionToken()}` }
  });
  if (!response.ok) throw new Error("Coven details unavailable");
  return response.json() as Promise<{ coven: unknown; members: unknown[] }>;
}

async function requestHuntStatus(huntId: string = "weekly") {
  const response = await fetch(`${API_BASE}/coven/hunt/status?hunt_id=${huntId}`, {
    headers: { "Authorization": `Bearer ${sessionToken()}` }
  });
  if (!response.ok) throw new Error("Hunt status unavailable");
  return response.json() as Promise<{ hunt_id: string; status: unknown }>;
}

function setForgeBusy(busy: boolean) {
  forgeUi.previewButton.disabled = busy;
  forgeUi.confirmButton.disabled = busy;
  forgeUi.reforgeButton.disabled = busy || !forgedWeapon?.id;
}

function setForgeError(error: unknown) {
  forgeState.error = error instanceof Error ? error.message : "The Anvil rejected the ritual.";
  forgeUi.status.textContent = forgeState.error;
  publishForgeState();
}

function renderForgePreview(preview: ForgePreview) {
  forgeUi.previewType.textContent = preview.weapon_type;
  forgeUi.previewDamage.textContent = String(preview.base_damage);
  forgeUi.previewWeight.textContent = String(preview.weight);
  forgeUi.previewDurability.textContent = String(preview.durability);
  forgeUi.previewAbility.textContent = preview.special_ability;
  forgeUi.previewSeed.textContent = String(preview.visual_seed);
  forgeState.weaponType = preview.weapon_type;
  forgeState.serverValuesDisplayed = true;
  forgeState.error = "";
}

function renderForgedWeapon(weapon: ForgedWeapon, forgeStones?: number) {
  forgeUi.resultName.textContent = weapon.name ?? `Soul-forged ${weapon.weapon_type}`;
  forgeUi.resultGeneration.textContent = `Generation ${weapon.generation ?? 0}${typeof forgeStones === "number" ? ` - Forge Stones ${forgeStones}` : ""}`;
  forgeUi.resultTraits.textContent = `Traits ${(weapon.traits ?? []).join(", ") || "-"}`;
  forgeState.generation = weapon.generation ?? 0;
  forgeState.scarState = forgeState.generation > 0 ? "scarred" : "fresh";
  forgeState.serverValuesDisplayed = true;
}

async function previewForgeRitual() {
  forgeState.previewRequested = true;
  forgeUi.status.textContent = "Previewing at the Anvil...";
  publishForgeState();
  setForgeBusy(true);
  try {
    const response = await requestForgePreview(API_BASE, sessionToken(), forgeEssences);
    forgePreview = response.preview;
    forgeState.previewFromServer = true;
    renderForgePreview(response.preview);
    forgeUi.status.textContent = "Preview sealed by server.";
  } catch (error) {
    setForgeError(error);
  } finally {
    setForgeBusy(false);
    publishForgeState();
  }
}

async function confirmForgeRitual() {
  forgeState.confirmRequested = true;
  forgeUi.status.textContent = "Forging weapon...";
  publishForgeState();
  setForgeBusy(true);
  try {
    const response = await requestForgeWeapon(API_BASE, sessionToken(), forgeEssences, "Crucible Greatblade");
    forgedWeapon = response.weapon;
    forgeState.confirmFromServer = true;
    renderForgePreview(response.weapon);
    renderForgedWeapon(response.weapon);
    forgeUi.status.textContent = "Weapon returned by server.";
  } catch (error) {
    setForgeError(error);
  } finally {
    setForgeBusy(false);
    publishForgeState();
  }
}

async function reforgeRitual() {
  if (!forgedWeapon?.id) return;
  forgeState.reforgeRequested = true;
  forgeUi.status.textContent = "Adding reforge scar...";
  publishForgeState();
  setForgeBusy(true);
  try {
    const response = await requestReforge(API_BASE, sessionToken(), forgedWeapon.id, reforgeEssence);
    forgedWeapon = response.weapon;
    forgeState.reforgeFromServer = true;
    renderForgePreview(response.weapon);
    renderForgedWeapon(response.weapon, response.forge_stones);
    forgeUi.status.textContent = "Reforge scar returned by server.";
  } catch (error) {
    setForgeError(error);
  } finally {
    setForgeBusy(false);
    publishForgeState();
  }
}

function openForgeShell() {
  if (!isInGame()) return;
  if (pillarMenuOpen) setPillarMenu(false);
  forgeUi.shell.hidden = false;
  document.body.classList.add("is-forging");
  forgeState.visible = true;
  forgeState.error = "";
  haptic("MENU_OPEN");
  publishForgeState();
  void previewForgeRitual();
}

function closeForgeShell() {
  forgeUi.shell.hidden = true;
  document.body.classList.remove("is-forging");
  forgeState.visible = false;
  publishForgeState();
}

async function playForgeDissolve(target?: HTMLElement) {
  visualState.forgeTransitionPlayed = true;
  visualState.forgeTransitionCount += 1;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  transitionLayer.classList.remove("is-active");
  void transitionLayer.offsetWidth;
  target?.classList.add("is-dissolving");
  transitionLayer.classList.add("is-active");
  await wait(860);
  target?.classList.remove("is-dissolving");
  transitionLayer.classList.remove("is-active");
}

async function enterGame() {
  // Stage 8: Rate limit check before entering game with visual feedback
  try {
    const response = await fetch(`${API_BASE}/player/sync`, {
      method: "POST",
      headers: { 
        "Authorization": `Bearer ${sessionToken()}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ x: 0, y: 0 })
    });
    
    if (response.status === 429) {
      // Stage 8: Visual feedback for rate limit rejection
      if (bindingError) {
        bindingError.textContent = "The Vein Tunnel is congested. Wait before entering the Crucible.";
        bindingError.hidden = false;
        bindingError.classList.add("rate-limited");
      }
      haptic("MENU_SELECT");
      return;
    }
  } catch (e) {
    // Stage 8: Continue on network error (may be local issue)
  }
  
  // Stage 8: Ritual transition per control protocol
  // Screen darkens, forge sigil appears, red cracks spread, ash particles rise
  bindingForm.hidden = true;
  setLandingMeshVisible(false);
  document.body.classList.remove("is-binding");
  document.body.classList.add("is-in-game");
  
  // Stage 8: Visual transition sequence
  transitionLayer.classList.remove("is-hidden");
  transitionLayer.style.background = "rgba(10, 10, 15, 0.95)";
  transitionLayer.style.opacity = "1";
  
  // Stage 8: Forge sigil overlay
  const sigil = document.createElement("div");
  sigil.className = "transition-sigil";
  sigil.style.cssText = `
    position: fixed;
    inset: 0;
    display: grid;
    place-content: center;
    z-index: 100;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.5s ease-in;
  `;
  sigil.innerHTML = `<div style="width: 150px; height: 150px; border: 2px solid rgba(212, 175, 55, 0.8); background: rgba(10, 10, 15, 0.9); clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);"></div>`;
  transitionLayer.appendChild(sigil);
  
  setTimeout(() => {
    sigil.style.opacity = "1";
  }, 50);
  
  // Stage 8: Red cracks spread
  const cracks = document.createElement("div");
  cracks.className = "transition-cracks";
  cracks.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 99;
    pointer-events: none;
    opacity: 0;
    background: 
      linear-gradient(to right, transparent 49%, rgba(139, 0, 0, 0.8) 50%, transparent 51%);
    transition: opacity 0.8s ease-in;
  `;
  transitionLayer.appendChild(cracks);
  
  setTimeout(() => {
    cracks.style.opacity = "1";
  }, 400);
  
  // Stage 8: Load in-game HUD after transition
  setTimeout(() => {
    transitionLayer.classList.add("is-hidden");
    transitionLayer.style.opacity = "0";
    transitionLayer.style.background = "";
    hudLayer.classList.remove("is-muted");
    
    // Stage 8: Initialize HUD system
    hud.showPillars();
    hud.render(combat.momentum, combat.stamina);
  }, 1500);
  
  haptic("MENU_SELECT");
  const sector = generateSector(0, 0);
  hud.setSector(sector.biome, "Sector 0:0");
  gameplayWorld.activate();
  await resonance.start(sector.biome);
}

async function openSoulBinding() {
  for (const trigger of enterTriggers) trigger.disabled = true;
  await playForgeDissolve(publicSiteRoot);
  setLandingMeshVisible(false);
  publicSiteRoot.hidden = true;
  document.body.classList.remove("is-public");
  document.body.classList.add("is-binding");
  bindingForm.hidden = false;
  bindingForm.classList.add("is-summoning");
  window.setTimeout(() => bindingForm.classList.remove("is-summoning"), 620);
  soulNameInput.focus();
  haptic("MENU_OPEN");
}

for (const trigger of enterTriggers) {
  trigger.addEventListener("click", () => {
    void openSoulBinding();
  });
}

binding.addEventListener("submit", async (event) => {
  event.preventDefault();
  bindingError.hidden = true;
  const username = soulName.value.trim();
  const password = soulSecret.value;
  try {
    const response = await fetch(`${API_BASE}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password, device_fingerprint: navigator.userAgent.slice(0, 120) })
    });
    const payload = await response.json() as { token?: string; error?: { message?: string } };
    if (!response.ok || !payload.token) throw new Error(payload.error?.message ?? "The binding failed.");
    sessionStorage.setItem("soulsmith.session", payload.token);
    sessionStorage.setItem("soulsmith.player", username);
    await playForgeDissolve(binding);
    await enterGame();
  } catch (error) {
    bindingError.textContent = error instanceof Error ? error.message : "The binding failed.";
    bindingError.hidden = false;
  }
});

window.addEventListener("keydown", (event) => {
  if (!isInGame()) return;
  if (event.key === "Escape") {
    event.preventDefault();
    closeAllShells();
    return;
  }
  if (event.key === "Tab") {
    event.preventDefault();
    haptic("MENU_OPEN");
    setPillarMenu(!pillarMenuOpen);
  }
  if (event.key.toLowerCase() === "f") {
    event.preventDefault();
    openForgeShell();
  }
  if (event.key.toLowerCase() === "b") {
    event.preventDefault();
    void openMarketShell();
  }
  if (event.key.toLowerCase() === "c") {
    event.preventDefault();
    void openBattlePassShell();
  }
  if (event.key.toLowerCase() === "g") {
    event.preventDefault();
    void openCovenShell();
  }
  if (event.key.toLowerCase() === "l") {
    event.preventDefault();
    void openSocialShell();
  }
  if (event.key.toLowerCase() === "q") {
    combat = applyAttack(combat, "heavy");
    haptic(combat.momentum >= 100 ? "MOMENTUM_100" : "HEAVY_ATTACK");
    resonance.setMomentum(combat.momentum);
    // Stage 6: Combat Visual Contract - heavy attack visual treatment
    applyMomentumVisuals(combat.momentum);
    applyStaminaVisuals(combat.stamina);
    createRuneMarker("heavy");
    createHitFeedback("heavy");
  }
});

forgeUi.previewButton.addEventListener("click", () => {
  void previewForgeRitual();
});

marketCloseButton.addEventListener("click", () => {
  closeAllShells();
});

marketTabs.forEach((tab) => {
  tab.addEventListener("click", () => {
    marketTabs.forEach((t) => t.classList.remove("is-active"));
    tab.classList.add("is-active");
    const type = tab.dataset.type ?? "all";
    void (async () => {
      try {
        const data = await requestMarketListings(type);
        marketListings.innerHTML = "";
        if (data.listings.length === 0) {
          marketListings.innerHTML = `<p class="market-empty">No listings at the altar.</p>`;
        } else {
          data.listings.slice(0, 10).forEach((listing: unknown) => {
            const item = document.createElement("div");
            item.className = "market-listing-item";
            item.innerHTML = `<strong>Market Item</strong><span>${JSON.stringify(listing).slice(0, 60)}</span>`;
            marketListings.appendChild(item);
          });
        }
      } catch (e) {
        marketStatus.textContent = "Failed to load listings.";
      }
    })();
  });
});

passCloseButton.addEventListener("click", () => {
  closeAllShells();
});

covenCloseButton.addEventListener("click", () => {
  closeAllShells();
});

socialCloseButton.addEventListener("click", () => {
  closeAllShells();
});

friendDiscoverButton.addEventListener("click", () => {
  void (async () => {
    try {
      socialStatus.textContent = "Discovering nearby Smiths...";
      const response = await fetch(`${API_BASE}/friends/discover`, {
        headers: { "Authorization": `Bearer ${sessionToken()}` }
      });
      if (!response.ok) throw new Error("Discovery failed");
      const data = await response.json() as { suggestions: unknown[] };
      friendsList.innerHTML = "";
      if (data.suggestions.length === 0) {
        friendsList.innerHTML = `<p class="friends-empty">No Smiths nearby to bond with.</p>`;
      } else {
        data.suggestions.forEach((suggestion: unknown) => {
          const item = document.createElement("div");
          item.className = "friend-item";
          item.innerHTML = `<strong>Nearby Smith</strong><span>${JSON.stringify(suggestion).slice(0, 60)}</span>`;
          friendsList.appendChild(item);
        });
      }
      socialStatus.textContent = "";
    } catch (e) {
      socialStatus.textContent = "Failed to discover Smiths.";
    }
  })();
});

forgeUi.confirmButton.addEventListener("click", () => {
  void confirmForgeRitual();
});

forgeUi.reforgeButton.addEventListener("click", () => {
  void reforgeRitual();
});

// Stage 8: Reforge visual shell event handlers
reforgeCancelButton?.addEventListener("click", () => {
  reforgePanel?.classList.add("is-hidden");
  forgeReforgeButton.disabled = forgedWeapon === null;
});

// Stage 8: Essence stash event handlers
essenceStashGrid?.addEventListener("click", (event) => {
  const slot = (event.target as HTMLElement).closest(".essence-stash-slot") as HTMLElement;
  if (!slot) return;
  
  const essenceType = slot.dataset.essence;
  const essenceTier = slot.dataset.tier;
  
  // Stage 8: Visual contract for essence selection from stash
  // This would update the forge socket selection
  if (forgeStatus) forgeStatus.textContent = `Selected ${essenceType} ${essenceTier} from stash for socket 0`;
});

// Stage 8: Inventory event handlers
inventoryCloseButton?.addEventListener("click", () => {
  inventoryShell?.classList.add("is-hidden");
});

inventoryGrid?.addEventListener("click", (event) => {
  const slot = (event.target as HTMLElement).closest(".inventory-slot") as HTMLElement;
  if (!slot) return;
  
  const slotNumber = slot.dataset.slot;
  const isEquipped = slot.classList.contains("weapon-equipped");
  const isEmpty = slot.classList.contains("empty");
  
  if (isEmpty) return;
  
  // Stage 8: Visual contract for inventory slot selection
  if (inventoryDetailName) inventoryDetailName.textContent = isEquipped ? "Void Cleaver (Equipped)" : "Weapon in slot " + slotNumber;
  if (inventoryDetailType) inventoryDetailType.textContent = isEquipped ? "Greatsword" : "Weapon Type";
  if (inventoryDetailDamage) inventoryDetailDamage.textContent = isEquipped ? "245" : "Damage";
  if (inventoryDetailWeight) inventoryDetailWeight.textContent = isEquipped ? "4.2 kg" : "Weight";
  if (inventoryDetailDurability) inventoryDetailDurability.textContent = isEquipped ? "82%" : "Durability";
  if (inventoryDetailGeneration) inventoryDetailGeneration.textContent = isEquipped ? "3" : "Generation";
  if (inventoryDetailTraits) inventoryDetailTraits.textContent = isEquipped ? "Soul Burn, Vorpal Edge" : "Traits";
});

inventoryEquipButton?.addEventListener("click", () => {
  // Stage 8: Visual contract for equip action
  if (forgeStatus) forgeStatus.textContent = "Weapon equipped.";
  inventoryShell?.classList.add("is-hidden");
});

inventoryReforgeButton?.addEventListener("click", () => {
  // Stage 8: Visual contract to open reforge from inventory
  inventoryShell?.classList.add("is-hidden");
  forgeShell?.removeAttribute("hidden");
  forgeReforgeButton.disabled = false;
  if (forgeStatus) forgeStatus.textContent = "Weapon selected for reforge.";
});

// Stage 8: Profile event handlers
profileCloseButton?.addEventListener("click", () => {
  profileShell?.classList.add("is-hidden");
});

reforgeEssenceSelect?.addEventListener("click", () => {
  // Stage 8: Visual contract for essence selection
  if (reforgePreviewText) reforgePreviewText.textContent = "Titan Uncommon - Scar will forge with +1 Generation, +5% Damage";
  if (reforgeDamageChange) reforgeDamageChange.textContent = "Damage: +5%";
  if (reforgeGenerationChange) reforgeGenerationChange.textContent = "Generation: +1";
  if (reforgeConfirmButton) {
    reforgeConfirmButton.disabled = false;
    reforgeConfirmButton.classList.remove("is-disabled");
  }
});

reforgeConfirmButton?.addEventListener("click", async () => {
  if (!forgedWeapon?.id) {
    if (forgeStatus) forgeStatus.textContent = "No weapon to reforge.";
    return;
  }
  try {
    if (reforgePreviewText) reforgePreviewText.textContent = "Scar forging...";
    if (reforgeConfirmButton) reforgeConfirmButton.disabled = true;
    const result = await requestReforge(API_BASE, "", forgedWeapon.id, reforgeEssence);
    if (forgeResultGeneration) forgeResultGeneration.textContent = result.weapon.generation?.toString() || "";
    reforgePanel?.classList.add("is-hidden");
    if (forgeStatus) forgeStatus.textContent = "Scar forged into weapon.";
    forgedWeapon = result.weapon;
  } catch (e) {
    if (reforgePreviewText) reforgePreviewText.textContent = "Scar rejected by the Anvil.";
  }
});

forgeUi.closeButton.addEventListener("click", () => {
  closeForgeShell();
});

window.addEventListener("pointerdown", () => {
  if (!isInGame()) return;
  combat = applyAttack(combat, "light");
  haptic("LIGHT_ATTACK");
  resonance.setMomentum(combat.momentum);
});

window.addEventListener("online", () => {
  offlineBadge.hidden = true;
});

window.addEventListener("offline", () => {
  offlineBadge.hidden = false;
});

engine.runRenderLoop(() => {
  const now = performance.now();
  if (now - lastEcho > 30000) {
    echo.record({ x: 0, y: 0, z: 0 });
    lastEcho = now;
  }
  hud.render(combat.momentum, combat.stamina);
  scene.render();
});

window.addEventListener("resize", () => engine.resize());

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/service-worker.js").catch(() => undefined);
}

fetch(`${API_BASE}/world/event`).then((response) => {
  if (!response.ok) throw new Error("event unavailable");
}).catch(() => {
  offlineBadge.hidden = false;
});

console.info(`Soulsmith booted for ${gpuTier} GPU tier.`);
}
